import { useEffect, useRef, useState } from 'react';
import type { PontoDoGrafico } from './GraficoLinha';

/**
 * Colunas verticais desenhadas à mão em SVG, par do GraficoLinha: uma série,
 * escala a partir do zero e o valor ao passar o mouse. Coluna fina (até 24px),
 * ponta arredondada e base reta; mês sem valor fica sem coluna, mas no eixo.
 */
export default function GraficoBarras({
  pontos,
  formatar,
  altura = 220,
  descricao,
  larguraMinima = 280,
}: {
  pontos: PontoDoGrafico[];
  formatar: (valor: number) => string;
  altura?: number;
  descricao: string;
  larguraMinima?: number;
}) {
  const caixa = useRef<HTMLDivElement>(null);
  const [largura, setLargura] = useState(640);
  const [foco, setFoco] = useState<number | null>(null);

  useEffect(() => {
    if (!caixa.current) return;
    const observador = new ResizeObserver(([entrada]) => setLargura(Math.max(larguraMinima, entrada.contentRect.width)));
    observador.observe(caixa.current);
    return () => observador.disconnect();
  }, [larguraMinima]);

  const valores = pontos.map((p) => p.valor ?? 0);
  const maximo = Math.max(0, ...valores);
  const minimo = Math.min(0, ...valores);

  const margem = { topo: 14, direita: 8, base: 26, esquerda: 72 };
  const areaL = largura - margem.esquerda - margem.direita;
  const areaA = altura - margem.topo - margem.base;

  // Marcas redondas: o teto é o próximo múltiplo "limpo" acima do maior valor.
  const passo = passoLimpo((maximo - minimo) / 4 || 1);
  const teto = Math.ceil(maximo / passo) * passo || passo;
  const piso = Math.floor(minimo / passo) * passo;
  const marcasY = Array.from({ length: Math.round((teto - piso) / passo) + 1 }, (_, k) => piso + k * passo);

  const y = (v: number) => margem.topo + (1 - (v - piso) / (teto - piso)) * areaA;
  const faixa = areaL / Math.max(1, pontos.length);
  const larguraBarra = Math.min(24, faixa * 0.6);
  const centro = (i: number) => margem.esquerda + faixa * i + faixa / 2;

  const eixo = (p: PontoDoGrafico) => p.rotuloCurto ?? p.rotulo;
  const larguraRotulo = Math.max(...pontos.map((p) => eixo(p).length), 1) * 6.6 + 12;
  const passoX = Math.max(1, Math.ceil(larguraRotulo / faixa));
  const emFoco = foco !== null ? pontos[foco] : undefined;

  return (
    <div ref={caixa} className="relative w-full">
      <svg width={largura} height={altura} role="img" aria-label={descricao} className="block">
        {marcasY.map((v) => (
          <g key={v}>
            <line x1={margem.esquerda} x2={largura - margem.direita} y1={y(v)} y2={y(v)} stroke="var(--color-border)" />
            <text x={margem.esquerda - 8} y={y(v)} textAnchor="end" dominantBaseline="middle" fontSize="11" fill="var(--color-muted)">
              {formatar(v)}
            </text>
          </g>
        ))}

        {pontos.map((p, i) =>
          (pontos.length - 1 - i) % passoX === 0 ? (
            <text key={p.rotulo + i} x={centro(i)} y={altura - 8} textAnchor="middle" fontSize="11" fill="var(--color-muted)">
              {eixo(p)}
            </text>
          ) : null,
        )}

        {pontos.map((p, i) => {
          if (!p.valor) return null;
          const x = centro(i) - larguraBarra / 2;
          const base = y(0);
          const topo = y(p.valor);

          return (
            <path
              key={i}
              d={coluna(x, base, topo, larguraBarra)}
              fill="var(--color-accent)"
              opacity={foco === null || foco === i ? 1 : 0.55}
            />
          );
        })}

        {pontos.map((_, i) => (
          <rect
            key={`alvo-${i}`}
            x={margem.esquerda + faixa * i}
            y={margem.topo}
            width={faixa}
            height={areaA}
            fill="transparent"
            onMouseEnter={() => setFoco(i)}
            onMouseLeave={() => setFoco(null)}
          />
        ))}
      </svg>

      {emFoco && (
        <div
          className="pointer-events-none absolute z-10 -translate-x-1/2 rounded-md border border-border bg-surface px-sm py-xs text-[12px] whitespace-nowrap shadow-md"
          style={{ left: Math.min(Math.max(centro(foco!), 80), largura - 80), top: Math.max(0, y(Math.max(emFoco.valor ?? 0, 0)) - 54) }}
        >
          <p className="text-muted">{emFoco.rotulo}</p>
          <p className="font-bold tabular-nums text-ink">{formatar(emFoco.valor ?? 0)}</p>
        </div>
      )}
    </div>
  );
}

/** 1, 2, 2,5 ou 5 vezes uma potência de dez: as marcas do eixo saem redondas. */
function passoLimpo(bruto: number): number {
  const potencia = 10 ** Math.floor(Math.log10(bruto));
  const fracao = bruto / potencia;
  const limpo = fracao <= 1 ? 1 : fracao <= 2 ? 2 : fracao <= 2.5 ? 2.5 : fracao <= 5 ? 5 : 10;

  return limpo * potencia;
}

/** Coluna com 4px de raio na ponta e base reta; para baixo, quando o valor é negativo. */
function coluna(x: number, base: number, topo: number, largura: number): string {
  const altura = Math.abs(base - topo);
  const r = Math.min(4, largura / 2, altura);
  const direita = x + largura;

  if (topo <= base) {
    return `M${x},${base} V${topo + r} Q${x},${topo} ${x + r},${topo} H${direita - r} Q${direita},${topo} ${direita},${topo + r} V${base} Z`;
  }

  return `M${x},${base} V${topo - r} Q${x},${topo} ${x + r},${topo} H${direita - r} Q${direita},${topo} ${direita},${topo - r} V${base} Z`;
}
